import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import { useVendorLookup } from "@hooks/shared/useSocketLookups";
import { buildShipmentsFilterSections, SHIPMENTS_DEFAULT_FILTERS } from "@config/filters/shipmentsFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";

export const SHIPMENTS_FILTER_ACTIONS_ID = "shipments-filter-actions";

/** Query params on the Shipments URL that are not filters (pagination is appended by the screen). */
const NON_FILTER_PARAMS = ["offset", "limit", "page"];

type Props = {
    isSearching: boolean;
    /** Existing Shipments fetch: receives the complete `?a=b&c=d` query string */
    searchFn: (queryString: string) => void;
};

/**
 * Shipments integration of the shared FilterBar (v2 search UI).
 * Screen key is ScreenOptionsEnum.SHIPPING ("shipping"), which is also the presets JSON key.
 */
export const ShipmentsFilterBar = (props: Props) => {
    const { searchFn } = props;
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const vendorLookup = useVendorLookup();

    const formatMessage = useCallback(
        (id: string) => (intl.messages && intl.messages[id] !== undefined ? intl.formatMessage({ id }) : id),
        [intl]
    );

    const sections = useMemo(
        () => buildShipmentsFilterSections({ supplierModel, userModel, lookups: vendorLookup, formatMessage }),
        [supplierModel, userModel, vendorLookup, formatMessage]
    );

    // Deep links (e.g. /shipments?order=ABC): read on mount and again on Back / Forward.
    // Pagination-only URL changes (offset / limit) do not count as a filter change.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, NON_FILTER_PARAMS);

    const { presets, onPresetsChange } = useFilterPresets(ScreenOptionsEnum.SHIPPING);

    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            const parts = toQueryStringParts(query);
            const search = parts.length ? `?${parts.join("&")}` : "";

            // Keep the URL in sync: Shipments pagination rebuilds its query from the URL params.
            const pathname = location.pathname.includes("ecomm") ? "/ecomm/shipments" : "/shipments";

            navigate({ pathname, search }, { replace: isFirstSearch.current });
            isFirstSearch.current = false;

            searchFn(search);
        },
        [location.pathname, navigate, searchFn, isFirstSearch]
    );

    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={ScreenOptionsEnum.SHIPPING}
            mode={"drawer"}
            defaultFilters={SHIPMENTS_DEFAULT_FILTERS}
            initialFilters={initialFilters}
            allLabel={"all_shipments"}
            actionsContainerId={SHIPMENTS_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default ShipmentsFilterBar;
